/**
 * 视频空间单条视频下载。
 * 解析持久化播放地址、按需拉详情、签名后转 blob 触发浏览器下载。
 */
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';

import { pickVideoSpaceSignOptions } from '@/pages/ipe/workbench/utils/videoSpaceDisplayAccess';
import {
  isLikelyStillImageUrl,
  isVideoFileUrl,
} from '@/pages/ipe/workbench/utils/videoStudioDetailUtils';


/**
 * 去除首尾空白，非字符串返回空串。
 *
 * @param {*} value 任意值
 * @returns {string}
 */
function trimUrl(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 解析视频空间下载用播放地址（持久化 url，排除 blob: / 静图海报）。
 *
 * @param {object|null|undefined} video 视频记录
 * @returns {string} 可用于下载的 URL，无则空串
 */
export function resolveVideoSpacePlayUrl(video) {
  if (!video || typeof video !== 'object') return '';

  const candidates = [
    video._playUrl,
    video.outputVideoUrl,
    video.videoUrl,
    video.url,
    video.src,
  ];

  for (const value of candidates) {
    const s = trimUrl(value);
    if (!s || s.startsWith('blob:')) continue;
    // 跳过海报/静图，避免选片映射后误把 jpg 当作成片
    if (isLikelyStillImageUrl(s) && !isVideoFileUrl(s)) continue;
    return s;
  }

  return '';
}



/**

 * 根据视频元数据与 blob 类型生成安全下载文件名。

 *

 * @param {object} video 视频记录

 * @param {Blob} [blob] 已拉取的 blob（用于推断扩展名）

 * @returns {string}

 */

function buildDownloadFileName(video, blob) {

  let fileName = String(video?.name || video?.fileName || video?.id || 'video').trim() || 'video';

  if (!/\.[a-zA-Z0-9]+$/.test(fileName)) {

    const ext = blob?.type === 'video/webm' ? 'webm' : 'mp4';

    fileName = `${fileName}.${ext}`;

  }

  return fileName.replace(/[\\/:*?"<>|]+/g, '_');

}



/**

 * 下载视频空间单个视频（签名后拉取 blob，与图片空间下载一致）。

 *

 * @param {object} video 视频记录

 * @param {object} [options]

 * @param {(message: string) => void} [options.onError] 失败回调

 * @returns {Promise<void>}

 */

export async function downloadVideoSpaceVideo(video, { onError } = {}) {

  try {

    // 1. 解析播放地址

    let playUrl = resolveVideoSpacePlayUrl(video);

    // 1.1 列表项无 URL 时按 id 拉详情再解析

    if (!playUrl && video?.id) {

      const { videoSpaceAPI } = await import('@/utils/api');

      const res = await videoSpaceAPI.getById(String(video.id));

      const remote = res?.video ?? res?.data ?? res;

      playUrl = resolveVideoSpacePlayUrl(remote);

    }

    if (!playUrl) {

      onError?.('暂无可下载的视频地址');

      return;

    }

    // 2. 签名拉 blob 并触发下载

    const blob = await resolveImageUrlToBlob(playUrl, pickVideoSpaceSignOptions(video, playUrl));

    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = objectUrl;

    link.download = buildDownloadFileName(video, blob);

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(objectUrl);

  } catch (error) {

    console.error('下载视频失败:', error);

    onError?.(error?.message || '下载失败，请重试');

  }

}

